import axios, { InternalAxiosRequestConfig } from 'axios';

/**
 * Global request cancellation manager.
 * 
 * Tracks all in-flight Axios requests and can cancel them all at once
 * (e.g., on route change). This ensures that when a user navigates away
 * from a page, all pending API requests from that page are cancelled
 * automatically — without needing to modify every individual component.
 * 
 * Setup: Call `setupAxiosInterceptors()` once at app startup (e.g., in index.tsx).
 * Then call `cancelAllPendingRequests()` on every route change.
 */

// Map of request ID -> AbortController for all in-flight requests
const pendingRequests = new Map<string, AbortController>();

let requestCounter = 0;

/**
 * Generate a unique ID for each request
 */
function generateRequestId(): string {
  return `req_${++requestCounter}_${Date.now()}`;
}

/**
 * Cancel all currently pending requests.
 * Call this on route change to abort any in-flight API requests.
 */
export function cancelAllPendingRequests(): void {
  pendingRequests.forEach((controller, id) => {
    controller.abort();
  });
  pendingRequests.clear();
}

/**
 * Get the count of currently pending requests (useful for debugging).
 */
export function getPendingRequestCount(): number {
  return pendingRequests.size;
}

/**
 * Install Axios interceptors that automatically track and manage
 * request cancellation. Call this once at app startup.
 * 
 * How it works:
 * 1. Request interceptor: Assigns an AbortController to every outgoing 
 *    request (unless one is already provided) and tracks it.
 * 2. Response interceptor: Removes completed requests from tracking.
 * 3. Error interceptor: Removes failed requests and silently handles 
 *    cancellation errors.
 */
export function setupAxiosInterceptors(): void {
  // Request interceptor — track every outgoing request
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const requestId = generateRequestId();

      // Store the request ID on the config so we can clean it up later
      (config as any).__requestId = requestId;

      // If the caller didn't supply their own signal, create one
      if (!config.signal) {
        const controller = new AbortController();
        config.signal = controller.signal;
        pendingRequests.set(requestId, controller);
      } else {
        // Caller provided their own signal (e.g., via useAbortController).
        // Wrap it so we can still cancel it globally on route change.
        const controller = new AbortController();
        const originalSignal: any = config.signal;

        // If the original signal aborts, abort our controller too
        if (originalSignal && typeof originalSignal.addEventListener === 'function') {
          originalSignal.addEventListener('abort', () => {
            controller.abort();
            pendingRequests.delete(requestId);
          });
        }

        config.signal = controller.signal;
        pendingRequests.set(requestId, controller);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor — clean up completed requests
  axios.interceptors.response.use(
    (response) => {
      const requestId = (response.config as any).__requestId;
      if (requestId) {
        pendingRequests.delete(requestId);
      }
      return response;
    },
    (error) => {
      const requestId = error?.config?.__requestId;
      if (requestId) {
        pendingRequests.delete(requestId);
      }
      return Promise.reject(error);
    }
  );
}
